// ─── Shared types & utilities for the Dump File Visualizer ──────────────────

export interface DumpPosition {
    _id?: string
    vehicleId?: string
    latitude?: number
    longitude?: number
    timestamp?: string
    speed?: number
    heading?: number
    accuracy?: number
    gpsFix?: boolean
    odometer?: number
    source?: string
    virtualOdometer?: number
    virtualSpeed?: number
    weight?: number
    totalFuel?: number
    externalMessageTimestamp?: string
}

export interface DumpStopCoordinates {
    latitude?: number
    longitude?: number
    source?: string
}

export interface DumpStop {
    stopId?: string
    city?: string
    name?: string
    name2?: string
    street?: string
    zipCode?: string
    countryCode?: string
    planArrive?: string
    planDepart?: string
    coordinates?: DumpStopCoordinates
    actions?: Array<{ kind?: string; freightIds?: string[]; actionId?: string }>
    status?: string
    mergeAndOrderKey?: string
    /** JSON string: Array<Array<{lat: number, lng: number}>> — multi-ring polygon geofence */
    multiPolygonGeofence?: string
}

/** Parses a multiPolygonGeofence JSON string into rings of [lat, lng] pairs for react-leaflet Polygon.
 *  Returns null if the string is missing or invalid. */
export function parseMultiPolygonGeofence(raw: string | undefined): Array<Array<[number, number]>> | null {
    if (!raw) return null
    try {
        const rings = JSON.parse(raw) as Array<Array<{ lat: number; lng: number }>>
        if (!Array.isArray(rings) || rings.length === 0) return null
        return rings.map(ring => ring.map(p => [p.lat, p.lng] as [number, number]))
    } catch (_) {
        return null
    }
}

/** Returns the centroid [lat, lng] of the first ring of a parsed multipolygon geofence. */
export function getGeofenceCenter(rings: Array<Array<[number, number]>>): [number, number] | null {
    const ring = rings[0]
    if (!ring || ring.length === 0) return null
    const lat = ring.reduce((sum, p) => sum + p[0], 0) / ring.length
    const lng = ring.reduce((sum, p) => sum + p[1], 0) / ring.length
    return [lat, lng]
}

export interface DumpTransport {
    transportId?: string
    stops?: DumpStop[]
    shipmentNumber?: string
}

export interface DumpVehicle {
    _id?: string
    numberPlate?: string
    externalVehicleId?: string
}

export interface DumpDriver {
    lastName?: string
    firstName?: string
    contactPhoneNr?: string
    externalDriverId?: string
}

export interface DumpTransportOrder {
    _id?: string
    transports?: DumpTransport[]
    assignedVehicle?: DumpVehicle
    assignedTrailer?: DumpVehicle
    assignedDriver?: DumpDriver
    companyId?: string
    companyName?: string
    created?: string
    externalTransportOrderId?: string
    comment?: string
    targetCompanyName?: string
}

export interface DumpRadiiConfig {
    arrivalRadiusInMeter?: number
    departureRadiusInMeter?: number
    earlyArrivalRadiusInMeter?: number
}

export interface DumpCompanyConfiguration {
    _id?: string
    name?: string
    '-configuration'?: {
        statusByVehiclePositionsConfig?: DumpRadiiConfig
    }
}

export interface GeoStatusDump {
    transportOrders?: DumpTransportOrder[]
    positions?: DumpPosition[]
    vehicles?: DumpVehicle[]
    companyConfigurations?: DumpCompanyConfiguration[]
    originalTransportOrders?: DumpTransportOrder[]
}

/** Extract geofence radii from the first company configuration in a dump. */
export function extractRadii(dump: GeoStatusDump): DumpRadiiConfig {
    const cfg = dump.companyConfigurations?.[0]?.['-configuration']?.statusByVehiclePositionsConfig
    return {
        arrivalRadiusInMeter: cfg?.arrivalRadiusInMeter,
        departureRadiusInMeter: cfg?.departureRadiusInMeter,
        earlyArrivalRadiusInMeter: cfg?.earlyArrivalRadiusInMeter,
    }
}

// ─── Segment computation ──────────────────────────────────────────────────────

export interface ComputedSegment {
    type: 'DRIVING' | 'STANDING'
    from: Date
    to: Date
    positions: DumpPosition[]
    distanceKm: number
    durationMinutes: number
    index: number
}

const STANDING_SPEED_KMH = 5

export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLng = (lng2 - lng1) * Math.PI / 180
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export function computeDistanceKm(positions: DumpPosition[]): number {
    let total = 0
    for (let i = 1; i < positions.length; i++) {
        const p = positions[i - 1]
        const q = positions[i]
        if (p.latitude != null && p.longitude != null && q.latitude != null && q.longitude != null) {
            total += haversineKm(p.latitude, p.longitude, q.latitude, q.longitude)
        }
    }
    return total
}

function isStandingPos(p: DumpPosition): boolean {
    // Use reported speed if available
    if (p.speed != null && Number.isFinite(p.speed)) {
        return p.speed < STANDING_SPEED_KMH
    }
    // If no speed data, assume driving (better UX than showing everything as standing)
    return false
}

export function computeSegments(positions: DumpPosition[]): ComputedSegment[] {
    const sorted = [...positions]
        .filter(p => p.latitude != null && p.longitude != null && p.timestamp != null)
        .sort((a, b) => new Date(a.timestamp!).getTime() - new Date(b.timestamp!).getTime())

    if (sorted.length < 2) return []

    const segments: ComputedSegment[] = []
    let start = 0
    let currentType: 'DRIVING' | 'STANDING' = isStandingPos(sorted[0]) ? 'STANDING' : 'DRIVING'

    for (let i = 1; i <= sorted.length; i++) {
        const isLast = i === sorted.length
        const nextType = isLast ? null : (isStandingPos(sorted[i]) ? 'STANDING' : 'DRIVING')

        if (isLast || nextType !== currentType) {
            const segPositions = sorted.slice(start, i)
            const from = new Date(segPositions[0].timestamp!)
            const to = new Date(segPositions[segPositions.length - 1].timestamp!)
            segments.push({
                type: currentType,
                from,
                to,
                positions: segPositions,
                distanceKm: computeDistanceKm(segPositions),
                durationMinutes: (to.getTime() - from.getTime()) / 60000,
                index: segments.length,
            })
            if (!isLast) {
                start = i
                currentType = nextType!
            }
        }
    }

    return segments
}

// ─── GPS Coverage chunking ────────────────────────────────────────────────────

export interface GpsCoverageChunk {
    start: Date
    end: Date
    positions: DumpPosition[]
    count: number
}

/** Auto-selects chunk size so we get at most `maxSegments` chunks. */
export function pickChunkMinutes(from: Date, to: Date, maxSegments = 100): number {
    const durationMs = to.getTime() - from.getTime()
    const options = [5, 10, 15, 30, 60, 120, 180, 360, 720, 1440]
    for (const mins of options) {
        if (durationMs / (mins * 60 * 1000) <= maxSegments) return mins
    }
    return 1440
}

export function buildGpsCoverageChunks(
    positions: DumpPosition[],
    from: Date,
    to: Date,
    chunkMinutes: number
): GpsCoverageChunk[] {
    const chunks: GpsCoverageChunk[] = []
    const chunkMs = chunkMinutes * 60 * 1000
    let t = from.getTime()
    const toMs = to.getTime()

    while (t < toMs) {
        const chunkStart = t
        const chunkEnd = Math.min(t + chunkMs, toMs)
        const chunkPositions = positions.filter(p => {
            if (!p.timestamp) return false
            const ts = new Date(p.timestamp).getTime()
            return ts >= chunkStart && ts < chunkEnd
        })
        chunks.push({
            start: new Date(chunkStart),
            end: new Date(chunkEnd),
            positions: chunkPositions,
            count: chunkPositions.length,
        })
        t += chunkMs
    }

    return chunks
}

// ─── Misc helpers ─────────────────────────────────────────────────────────────

export function formatTs(ts?: string | Date | null): string {
    if (!ts) return '—'
    try {
        const d = typeof ts === 'string' ? new Date(ts) : ts
        return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'medium' })
    } catch {
        return String(ts)
    }
}

export function formatTsWithTz(ts?: string | Date | null): string {
    if (!ts) return '—'
    try {
        const d = typeof ts === 'string' ? new Date(ts) : ts
        const formatted = d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'medium' })
        const tz = d.toLocaleString('en-US', { timeZoneName: 'short' }).split(', ')[1]?.split(' ').slice(-1)[0] ?? ''
        return tz ? `${formatted} ${tz}` : formatted
    } catch {
        return String(ts)
    }
}

export function formatDuration(minutes: number): string {
    if (minutes < 60) return `${minutes.toFixed(0)} min`
    const h = Math.floor(minutes / 60)
    const m = Math.round(minutes % 60)
    return `${h}h ${m}m`
}

